/**
 * Cancel Booking Screen -- shows policy tier, refund preview, reason select, and confirm.
 */
import { useEffect, useState } from 'react'
import { View, Text, ActivityIndicator } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { impact, notification, ImpactStyle, NotificationType } from '@/lib/utils/haptics'
import { FONTS, COLORS, SPACING, RADIUS } from '@/lib/brand'
import { CalendarOff } from '@/components/ui/icons'
import { useSemanticColors, withAlpha } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { useAuth } from '@/lib/hooks/use-auth'
import { useBooking } from '@/lib/hooks/use-booking'
import { useCancellationPreview, useCancelBooking } from '@/lib/hooks/use-cancellation'
import { useBookingStore } from '@/lib/stores/booking.store'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { Select, type SelectOption } from '@/components/ui/Select'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { Image } from '@/components/ui/Image'
import { CancellationPolicyCard } from '@/components/booking/CancellationPolicyCard'
import { RefundSummary } from '@/components/booking/RefundSummary'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { formatBookingDate } from '@/lib/utils/dates'
import { useProductTelemetryScroll } from '@/lib/hooks/use-product-telemetry-scroll'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'
import type { CancellationReason } from '@/lib/types/cancellation.types'

function formatDate(iso: string): string {
  return formatBookingDate(iso, 'es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function refundBucket(cents: number | null | undefined): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return 'unknown'
  if (cents <= 0) return 'zero'
  if (cents < 2500) return 'lt_25'
  if (cents < 5000) return '25_49'
  if (cents < 10000) return '50_99'
  if (cents < 20000) return '100_199'
  return 'gte_200'
}

const REASON_OPTIONS: SelectOption<CancellationReason>[] = [
  { value: 'plans_changed', label: '' },
  { value: 'found_alternative', label: '' },
  { value: 'vehicle_issue', label: '' },
  { value: 'personal', label: '' },
  { value: 'other', label: '' },
]

export default function CancelBookingScreen() {
  const { bookingId } = useLocalSearchParams<{ bookingId: string }>()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const router = useRouter()
  const { user } = useAuth()

  const { data: booking, isLoading: bookingLoading, error: bookingError } = useBooking(bookingId)
  const {
    data: preview,
    isLoading: previewLoading,
    error: previewError,
  } = useCancellationPreview(bookingId)
  const { cancelBooking, isPending } = useCancelBooking()

  const [reason, setReason] = useState<CancellationReason | null>(null)
  const [otherReason, setOtherReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Localize reason labels
  const localizedOptions: SelectOption<CancellationReason>[] = REASON_OPTIONS.map((opt) => ({
    ...opt,
    label: t(`cancellation.reason_${opt.value}`),
  }))

  const isLoading = bookingLoading || previewLoading
  const canConfirm = !!reason && (reason !== 'other' || otherReason.trim().length > 0)
  const isCancelling = isPending || isSubmitting
  const handleCancelScroll = useProductTelemetryScroll('cancel.screen.scroll')

  useEffect(() => {
    if (!booking || !preview) return
    productTelemetryService.recordEvent('booking_step', 'booking.cancel_preview_viewed', {
      elementId: 'cancel.screen',
      metadata: {
        policy: preview.policy,
        refundBucket: refundBucket(preview.refundCents),
        cancellationFeeBucket: refundBucket(preview.cancellationFeeCents),
        role:
          booking.hostId === user?.id ? 'host' : booking.guestId === user?.id ? 'guest' : 'unknown',
      },
    })
  }, [booking, preview, user?.id])

  const handleReasonChange = (nextReason: CancellationReason | null) => {
    setReason(nextReason)
    if (!nextReason) return
    productTelemetryService.recordEvent('booking_step', 'booking.cancel_reason_selected', {
      elementId: 'cancel.reason-select',
      elementType: 'select',
      metadata: { reasonType: nextReason === 'other' ? 'other' : 'preset' },
    })
  }

  const handleConfirm = async () => {
    if (!canConfirm || !bookingId || !user?.id || isCancelling) return
    // Destructive confirm: answer the tap now, not when the mutation resolves.
    impact(ImpactStyle.Heavy)

    try {
      setIsSubmitting(true)
      const finalReason = reason === 'other' ? otherReason.trim() : reason
      productTelemetryService.recordEvent('booking_step', 'booking.cancel_confirm_started', {
        elementId: 'cancel.confirm-button',
        elementType: 'button',
        metadata: {
          reasonType: reason === 'other' ? 'other' : 'preset',
          refundBucket: refundBucket(preview?.refundCents),
        },
      })
      const result = await cancelBooking({
        userId: user.id,
        bookingId,
        reason: finalReason,
      })
      productTelemetryService.recordEvent('booking_step', 'booking.cancel_confirm_succeeded', {
        elementId: 'cancel.confirm-button',
        elementType: 'button',
        metadata: {
          refundStatus: result.refundStatus,
          manualRequired: result.manualRequired ?? false,
        },
      })
      void notification(NotificationType.Success)
      toast.success(t(`cancellation.refund_status_${result.refundStatus}` as const))
      // Wipe any in-flight booking draft so the user doesn't see a stale
      // quote from the just-cancelled rental if they open a new booking next.
      useBookingStore.getState().reset()
      router.replace('/(tabs)/trips')
    } catch (err) {
      productTelemetryService.recordEvent('booking_step', 'booking.cancel_confirm_failed', {
        elementId: 'cancel.confirm-button',
        elementType: 'button',
      })
      // Surface failures that used to be silent haptic-only catches; users got a buzz with
      // no toast and no Sentry signal explaining why cancellation failed
      // (RLS denial, payment-refund webhook timeout, etc).
      void notification(NotificationType.Error)
      toast.error(errorMessage(err, t('cancellation.error_failed')))
      captureError(err, { screen: 'booking/cancel', action: 'cancel-booking' })
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <ScreenContainer scroll={false}>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator size="large" color={COLORS.coral.DEFAULT} />
        </View>
      </ScreenContainer>
    )
  }

  if (bookingError || previewError || !booking || !preview) {
    return (
      <ScreenContainer card={false} scroll={false}>
        <View style={{ flex: 1 }}>
          <ScreenHeader
            title={t('cancellation.title')}
            backTestID="cancel.back-button"
            style={{ paddingHorizontal: SPACING.md }}
          />
          {/* Same composition as the other failure states in this flow
              (/booking/payment-complete): tinted status disc, title, one body
              paragraph, then the way out. This one had no icon at all and a
              flat 16pt gap between every part, so it read as a different
              component doing the same job. Horizontal padding is SPACING.lg
              so the CTA lands on the same 24pt edge as every other
              screen-level primary. */}
          <View
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              paddingHorizontal: SPACING.lg,
              paddingVertical: SPACING.xl,
            }}
          >
            <View
              style={{
                width: 80,
                height: 80,
                borderRadius: RADIUS.full,
                backgroundColor: withAlpha(colors.info, 0.08),
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: SPACING.lg,
              }}
            >
              <CalendarOff size={40} color={colors.info} strokeWidth={1.5} />
            </View>
            {/* Marcador de estado: esta ruta no pudo mostrar lo que existe para
              mostrar. Lo lee screen-matrix. Ver EmptyState.unavailable. */}
            <View testID="screen-state.unavailable" style={{ width: 0, height: 0 }} />
            <Text
              style={{
                fontSize: 20,
                lineHeight: 28,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
                textAlign: 'center',
              }}
            >
              {t('cancellation.unavailable_title')}
            </Text>
            <Text
              style={{
                fontSize: 15,
                lineHeight: 22,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                textAlign: 'center',
                marginTop: SPACING.sm,
                maxWidth: 360,
              }}
            >
              {t('cancellation.unavailable_body')}
            </Text>
            <View style={{ marginTop: SPACING.xl, width: '100%' }}>
              <Button
                title={t('cancellation.back_to_trips')}
                variant="primary"
                size="lg"
                onPress={() => router.replace('/(tabs)/trips')}
                testID="cancel.back-to-trips"
              />
            </View>
          </View>
        </View>
      </ScreenContainer>
    )
  }

  return (
    <ScreenContainer card={false} scroll={false}>
      <View style={{ flex: 1 }}>
        <KeyboardAwareView
          includeTopInset={false}
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingBottom: insets.bottom + SPACING['3xl'] * 3 }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          onScroll={handleCancelScroll}
          scrollEventThrottle={64}
        >
          <ScreenHeader
            title={t('cancellation.title')}
            backTestID="cancel.back-button"
            style={{ paddingHorizontal: SPACING.md }}
          />

          {/* Booking summary card */}
          {booking && (
            <View
              style={{
                marginHorizontal: SPACING.md,
                padding: SPACING.md,
                backgroundColor: colors.surface,
                borderRadius: RADIUS.lg,
                borderWidth: 1,
                borderColor: colors.border,
                flexDirection: 'row',
                gap: SPACING.md,
              }}
            >
              {booking.vehiclePrimaryImageUrl ? (
                <Image
                  source={booking.vehiclePrimaryImageUrl}
                  width={80}
                  height={60}
                  contentFit="cover"
                  borderRadius={8}
                  accessibilityLabel={booking.vehicleTitle}
                />
              ) : null}
              <View style={{ flex: 1, justifyContent: 'center' }}>
                <Text
                  style={{
                    fontSize: 16,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textPrimary,
                  }}
                  numberOfLines={1}
                >
                  {booking.vehicleTitle}
                </Text>
                <Text
                  style={{
                    fontSize: 13,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                    marginTop: SPACING.xs,
                  }}
                >
                  {formatDate(booking.startAt)} — {formatDate(booking.endAt)}
                </Text>
              </View>
            </View>
          )}

          {/* Policy card */}
          {preview && (
            <View style={{ marginTop: SPACING.md, marginHorizontal: SPACING.md }}>
              <CancellationPolicyCard
                activePolicy={preview.policy}
                hoursBeforeStart={preview.hoursBeforeStart}
              />
            </View>
          )}

          {/* Refund summary */}
          {preview && (
            <View style={{ marginTop: SPACING.md, marginHorizontal: SPACING.md }}>
              <RefundSummary
                refundCents={preview.refundCents}
                cancellationFeeCents={preview.cancellationFeeCents}
                policy={preview.policy}
              />
            </View>
          )}

          {/* Reason */}
          <View style={{ marginTop: SPACING.md, marginHorizontal: SPACING.md }}>
            <Select
              value={reason}
              onChange={handleReasonChange}
              options={localizedOptions}
              label={t('cancellation.reason_label')}
              placeholder={t('cancellation.reason_placeholder')}
              sheetTitle={t('cancellation.reason_label')}
              testID="cancel.reason-select"
            />
          </View>

          {/* Other reason text input */}
          {reason === 'other' && (
            <View style={{ marginTop: SPACING.sm, marginHorizontal: SPACING.md }}>
              <TextInput
                value={otherReason}
                onChangeText={setOtherReason}
                placeholder={t('cancellation.reason_placeholder')}
                multiline
                numberOfLines={3}
                maxLength={500}
                testID="cancel.other-reason-input"
                accessibilityLabel={t('cancellation.reason_placeholder')}
              />
            </View>
          )}
        </KeyboardAwareView>

        {/* Bottom CTA */}
        <View
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: SPACING.md,
            paddingBottom: insets.bottom + SPACING.md,
            backgroundColor: colors.background,
            borderTopWidth: 1,
            borderTopColor: colors.border,
          }}
        >
          <Button
            title={t('cancellation.confirm_button')}
            variant="primary"
            size="lg"
            onPress={handleConfirm}
            disabled={!canConfirm || isCancelling}
            loading={isCancelling}
            testID="cancel.confirm-button"
            accessibilityRole="button"
            accessibilityLabel={t('cancellation.confirm_button')}
          />
        </View>
      </View>
    </ScreenContainer>
  )
}
